// The payments report on the fee slip screen: what was paid, when, and for how much, so a
// firm can be told what has gone out on its behalf.
//
// Dates here are plain days - "2026-09-12" - and never Date objects carrying a time. A
// payment made on the 12th in Melbourne must not land on the 11th because something turned
// it into UTC on the way, and a report going to a firm cannot be a day out.

import { escapePrintHtml } from "./clerkStatements.js";

export const PAYMENT_PERIODS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const PAYMENT_TYPES = [
  { value: "fee_slip", label: "Fee slip" },
  { value: "disbursement", label: "Disbursement invoice" },
];

export const paymentTypeLabel = (value) =>
  PAYMENT_TYPES.find((type) => type.value === value)?.label || "Fee slip";

const pad = (value) => String(value).padStart(2, "0");
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDate(value) {
  const match = ISO_DATE.exec(String(value || ""));
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

// Today where the person is sitting, not in UTC.
export function todayIso(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const toUtc = (iso) => {
  const [year, month, day] = String(iso).split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

const fromUtc = (ms) => {
  const date = new Date(ms);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

export const addDays = (iso, days) => fromUtc(toUtc(iso) + days * 86400000);

// Monday, so a week or fortnight offered by default starts where a working week does.
export function startOfWeek(iso) {
  const weekday = new Date(toUtc(iso)).getUTCDay();
  return addDays(iso, weekday === 0 ? -6 : 1 - weekday);
}

export const startOfMonth = (iso) => `${String(iso).slice(0, 7)}-01`;

// Day zero of the next month is the last day of this one, so month lengths and February in
// a leap year look after themselves.
export function endOfMonth(iso) {
  const [year, month] = String(iso).split("-").map(Number);
  return fromUtc(Date.UTC(year, month, 0));
}

export function formatDay(iso) {
  if (!isIsoDate(iso)) return "";
  return new Date(toUtc(iso)).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

// The period a report covers, from the date the person chose. A week or fortnight runs from
// that date, so a fortnight can be lined up with a firm's own payment cycle; months and
// years are calendar ones.
export function periodRange(kind, anchorIso) {
  const anchor = isIsoDate(anchorIso) ? anchorIso : todayIso();
  if (kind === "weekly") {
    return { start: anchor, end: addDays(anchor, 6), label: `Week from ${formatDay(anchor)}` };
  }
  if (kind === "fortnightly") {
    return { start: anchor, end: addDays(anchor, 13), label: `Fortnight from ${formatDay(anchor)}` };
  }
  if (kind === "monthly") {
    const start = startOfMonth(anchor);
    return {
      start,
      end: endOfMonth(anchor),
      label: new Date(toUtc(start)).toLocaleDateString("en-AU", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }),
    };
  }
  if (kind === "yearly") {
    const year = String(anchor).slice(0, 4);
    return { start: `${year}-01-01`, end: `${year}-12-31`, label: year };
  }
  return { start: anchor, end: anchor, label: formatDay(anchor) };
}

// The same period one step earlier or later.
export function shiftPeriod(kind, anchorIso, step) {
  const anchor = isIsoDate(anchorIso) ? anchorIso : todayIso();
  if (kind === "weekly") return addDays(anchor, 7 * step);
  if (kind === "fortnightly") return addDays(anchor, 14 * step);
  if (kind === "monthly") {
    const [year, month] = anchor.split("-").map(Number);
    return fromUtc(Date.UTC(year, month - 1 + step, 1));
  }
  if (kind === "yearly") return `${Number(anchor.slice(0, 4)) + step}-01-01`;
  return addDays(anchor, step);
}

export function paymentsInPeriod(payments = [], range = {}) {
  return (Array.isArray(payments) ? payments : [])
    .filter((payment) => {
      const day = String(payment?.paid_on || "").slice(0, 10);
      return isIsoDate(day) && day >= range.start && day <= range.end;
    })
    .sort((a, b) => String(a.paid_on).localeCompare(String(b.paid_on))
      || String(a.client_name || "").localeCompare(String(b.client_name || "")));
}

export function summarisePayments(payments = []) {
  const totals = { fee_slip: 0, disbursement: 0, total: 0, count: 0 };
  for (const payment of Array.isArray(payments) ? payments : []) {
    const amount = Number(payment?.amount_paid) || 0;
    const type = payment?.payment_type === "disbursement" ? "disbursement" : "fee_slip";
    totals[type] = Math.round((totals[type] + amount) * 100) / 100;
    totals.total = Math.round((totals.total + amount) * 100) / 100;
    totals.count += 1;
  }
  return totals;
}

export const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

export const paymentsForRow = (payments = [], rowId = "") =>
  (Array.isArray(payments) ? payments : []).filter((payment) => payment?.source_ref === rowId);

// What the payment box starts with, per kind of row on the screen.
export function defaultPaymentType(row = {}) {
  if (row.source !== "email") return "fee_slip";
  if (row.emailKind === "disbursement") return "disbursement";
  if (row.emailKind === "fee_slip") return "fee_slip";
  // In a clerk's list by what its email says: one of that clerk's barristers' invoices.
  if (row.clerkFrom) return "fee_slip";
  // An emailed item nobody has sorted yet: the type has to be chosen before it is paid.
  return "";
}

// Who sent it to us, as opposed to who billed for it. A statement row has no person behind
// it - it came off the clerk's own statement - so the clerk is named instead.
export function defaultSentBy(row = {}) {
  const sentBy = String(row.sentBy || "").trim();
  if (sentBy) return sentBy;
  if (row.source === "clerk_statement") return `Clerk statement - ${row.clerkName || "clerk"}`;
  return "";
}

// What is still owing on a row: the invoice amount less whatever has already been recorded
// against it. Never negative, always to the cent.
export function amountOwing(row = {}, payments = []) {
  const invoice = Number.parseFloat(row.invoiceAmount) || 0;
  const paid = paymentsForRow(payments, row.id)
    .reduce((sum, payment) => sum + (Number(payment.amount_paid) || 0), 0);
  return Math.max(0, Math.round((invoice - paid) * 100) / 100);
}

// The printable report. Built here rather than in the page so its escaping can be tested:
// client names carry apostrophes and ampersands, and this document goes to a firm.
export function buildPaymentReportHtml({
  firmName = "",
  range = {},
  payments = [],
  preparedAt = new Date(),
} = {}) {
  const totals = summarisePayments(payments);
  const body = payments.length
    ? payments.map((payment) => `
      <tr>
        <td>${escapePrintHtml(formatDay(String(payment.paid_on || "").slice(0, 10)))}</td>
        <td>${escapePrintHtml(payment.client_name || "")}</td>
        <td>${escapePrintHtml(payment.sent_by || "")}</td>
        <td>${escapePrintHtml(paymentTypeLabel(payment.payment_type))}</td>
        <td>${escapePrintHtml(payment.invoice_ref || "")}</td>
        <td class="amount">${escapePrintHtml(formatMoney(payment.amount_paid))}</td>
      </tr>`).join("")
    : "<tr><td colspan=\"6\" class=\"empty\">No payments were recorded in this period.</td></tr>";

  return `<!doctype html><html><head><title>${escapePrintHtml(firmName)} - Payments made</title><style>
    body{font-family:Arial,sans-serif;color:#172033;margin:28px}
    h1{font-size:22px;margin-bottom:6px}
    .meta{color:#64748b;font-size:12px;margin-bottom:18px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;vertical-align:top}
    th{background:#f1f5f9}
    .amount{text-align:right}
    .empty{color:#64748b;text-align:center}
    .totals{margin-top:16px;width:auto;margin-left:auto}
    .totals td{border:none;padding:4px 8px;font-size:12px}
    .totals .grand td{font-weight:700;font-size:15px;border-top:2px solid #172033}
    @page{size:A4 landscape;margin:12mm}
  </style></head><body>
    <h1>${escapePrintHtml(firmName)} &mdash; Payments made</h1>
    <div class="meta">
      <strong>Period:</strong> ${escapePrintHtml(range.label || "")}
      (${escapePrintHtml(formatDay(range.start))} to ${escapePrintHtml(formatDay(range.end))})<br>
      <strong>Payments:</strong> ${totals.count}<br>
      <strong>Prepared:</strong> ${escapePrintHtml(preparedAt.toLocaleString("en-AU"))}
    </div>
    <table>
      <thead><tr>
        <th>Date paid</th><th>Client</th><th>Sent by</th><th>Type</th><th>Invoice</th><th>Amount paid</th>
      </tr></thead>
      <tbody>${body}</tbody>
    </table>
    <table class="totals">
      <tr><td>Fee slips</td><td class="amount">${escapePrintHtml(formatMoney(totals.fee_slip))}</td></tr>
      <tr><td>Disbursement invoices</td><td class="amount">${escapePrintHtml(formatMoney(totals.disbursement))}</td></tr>
      <tr class="grand"><td>Total paid</td><td class="amount">${escapePrintHtml(formatMoney(totals.total))}</td></tr>
    </table>
  </body></html>`;
}
